import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BACK_TO_MENU,
  MAIN_MENU,
  PAGE_GREETINGS,
  SUBTOPIC_RESPONSES,
  TOOLTIP_MESSAGE,
  WELCOME_MESSAGE,
  type AssistantOption,
  type AssistantTopic,
} from '../data/siteAssistantKnowledge';

interface ChatMessage {
  id: string;
  role: 'bot' | 'user';
  text: string;
  path?: string;
  pathLabel?: string;
}

const TOOLTIP_STORAGE_KEY = 'puremedify-assistant-tooltip-seen';

const SiteAssistant = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [options, setOptions] = useState<AssistantOption[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<number | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, options]);

  useEffect(() => {
    const hasSeenTooltip = localStorage.getItem(TOOLTIP_STORAGE_KEY);
    if (!hasSeenTooltip) {
      const timer = window.setTimeout(() => setShowTooltip(true), 1200);
      return () => window.clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        window.clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const dismissTooltip = () => {
    setShowTooltip(false);
    localStorage.setItem(TOOLTIP_STORAGE_KEY, 'true');
  };

  const showMainMenu = () => {
    setOptions(
      MAIN_MENU.map((topic) => ({
        id: topic.id,
        label: `${topic.icon} ${topic.label}`,
      }))
    );
  };

  const getWelcomeMessage = () => PAGE_GREETINGS[location.pathname] ?? WELCOME_MESSAGE;

  const greetUser = () => {
    if (hasGreeted) return;
    setHasGreeted(true);
    setMessages([{ id: 'welcome', role: 'bot', text: getWelcomeMessage() }]);
    showMainMenu();
  };

  const openAssistant = () => {
    dismissTooltip();
    setIsOpen(true);

    if (!hasGreeted) {
      greetUser();
      return;
    }

    if (options.length === 0) {
      showMainMenu();
    }
  };

  const closeAssistant = () => {
    setIsOpen(false);
  };

  const addBotMessage = (text: string, path?: string, pathLabel?: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `bot-${Date.now()}-${Math.random()}`,
        role: 'bot',
        text,
        path,
        pathLabel,
      },
    ]);
  };

  const runBotReply = (reply: () => void, delay = 600) => {
    setIsTyping(true);
    typingTimeoutRef.current = window.setTimeout(() => {
      setIsTyping(false);
      reply();
    }, delay);
  };

  const handleTopicSelect = (topic: AssistantTopic) => {
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', text: topic.label },
    ]);

    runBotReply(() => {
      addBotMessage(topic.response, topic.path, topic.pathLabel);
      setOptions(topic.children?.length ? [...topic.children, BACK_TO_MENU] : [BACK_TO_MENU]);
    });
  };

  const handleSubtopicSelect = (option: AssistantOption) => {
    const subtopic = SUBTOPIC_RESPONSES[option.id];
    if (!subtopic) return;

    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', text: option.label },
    ]);

    runBotReply(() => {
      addBotMessage(subtopic.response, subtopic.path, subtopic.pathLabel);
      setOptions([BACK_TO_MENU]);
    });
  };

  const handleOptionClick = (option: AssistantOption) => {
    if (option.id === 'main-menu') {
      setMessages((prev) => [
        ...prev,
        { id: `user-${Date.now()}`, role: 'user', text: 'Main menu' },
      ]);

      runBotReply(() => {
        addBotMessage('Sure! Here are the things I can help you with:');
        showMainMenu();
      }, 400);
      return;
    }

    const topic = MAIN_MENU.find((item) => item.id === option.id);
    if (topic) {
      handleTopicSelect(topic);
      return;
    }

    handleSubtopicSelect(option);
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      {/* First-visit tooltip */}
      {showTooltip && !isOpen && (
        <div className="relative max-w-[220px] animate-assistant-in rounded-2xl border border-lochmara-blue/10 bg-white px-4 py-3 shadow-xl">
          <button
            type="button"
            onClick={dismissTooltip}
            className="absolute right-2 top-2 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Dismiss tooltip"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <p className="pr-4 font-redhat text-sm font-medium text-gray-700">{TOOLTIP_MESSAGE}</p>
          <button
            type="button"
            onClick={openAssistant}
            className="mt-2 font-redhat text-xs font-semibold text-lochmara-blue hover:underline"
          >
            Open assistant →
          </button>
          <span className="absolute -bottom-2 right-6 h-4 w-4 rotate-45 border-b border-r border-lochmara-blue/10 bg-white" />
        </div>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          className="flex w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-lochmara-blue/10 bg-white shadow-2xl animate-assistant-in md:w-96"
          role="dialog"
          aria-label="PureMedify site assistant"
        >
          <div className="flex items-center justify-between bg-gradient-to-r from-lochmara-blue to-brand px-4 py-3 text-white">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-lg">
                ✦
              </span>
              <div>
                <p className="font-ubuntu text-sm font-bold">PureMedify Assistant</p>
                <p className="font-redhat text-xs text-white/80">Here to guide you</p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeAssistant}
              className="rounded-full p-1.5 transition hover:bg-white/20"
              aria-label="Close assistant"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex max-h-80 flex-col gap-3 overflow-y-auto bg-gradient-to-b from-blue-50/50 to-white px-4 py-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 font-redhat text-sm leading-relaxed shadow-sm ${
                    message.role === 'user'
                      ? 'rounded-br-md bg-lochmara-blue text-white'
                      : 'rounded-bl-md bg-white text-gray-700 ring-1 ring-gray-100'
                  }`}
                >
                  <p className="whitespace-pre-line">{message.text}</p>
                  {message.path && message.pathLabel && (
                    <Link
                      to={message.path}
                      onClick={closeAssistant}
                      className={`mt-2 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition ${
                        message.role === 'user'
                          ? 'bg-white/20 text-white hover:bg-white/30'
                          : 'bg-lochmara-blue/10 text-lochmara-blue hover:bg-lochmara-blue/20'
                      }`}
                    >
                      {message.pathLabel}
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-white px-4 py-3 shadow-sm ring-1 ring-gray-100">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-lochmara-blue [animation-delay:0ms]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-lochmara-blue [animation-delay:150ms]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-lochmara-blue [animation-delay:300ms]" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {options.length > 0 && !isTyping && (
            <div className="border-t border-gray-100 bg-white px-3 py-3">
              <p className="mb-2 px-1 font-redhat text-xs font-medium text-gray-400">Quick options</p>
              <div className="flex max-h-36 flex-wrap gap-2 overflow-y-auto">
                {options.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleOptionClick(option)}
                    className={`rounded-full px-3 py-1.5 font-redhat text-xs font-medium transition ${
                      option.id === 'main-menu'
                        ? 'border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                        : 'border border-lochmara-blue/20 bg-lochmara-blue/5 text-lochmara-blue hover:border-lochmara-blue/40 hover:bg-lochmara-blue/10'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={isOpen ? closeAssistant : openAssistant}
        className="relative group flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-lochmara-blue to-brand text-white shadow-lg transition hover:scale-105 hover:from-brand hover:to-malachite-green hover:shadow-xl md:h-16 md:w-16"
        aria-label={isOpen ? 'Close assistant' : 'Open assistant'}
      >
        {isOpen ? (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        ) : (
          <>
            <svg className="h-7 w-7 transition group-hover:scale-110" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
            <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-malachite-green text-[10px] font-bold ring-2 ring-white">
              ✦
            </span>
          </>
        )}
      </button>

      <style>{`
        @keyframes assistant-in {
          0% { opacity: 0; transform: translateY(16px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-assistant-in { animation: assistant-in 0.25s cubic-bezier(.4,0,.2,1) both; }
      `}</style>
    </div>
  );
};

export default SiteAssistant;
